import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BarChart3, Newspaper, Image as ImageIcon, Heart, GraduationCap, HandHeart, Mail, Settings as SettingsIcon, Sparkles, Crown, FolderKanban, Calendar, Tv, FileText, LogOut, Search, Bell, Activity, ChevronRight, Menu, X, Banknote, Users, Building2, BookOpen, Type, Gift, CircleDollarSign, Home, FileCode, DatabaseBackup, Globe, MailCheck, Eye, ListTree, FilePlus, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const NAV = [
  { section: 'Genel', items: [
    { to: '/admin', label: 'Pano', icon: LayoutDashboard, end: true },
    { to: '/admin/analytics', label: 'Analitik', icon: BarChart3 },
    { to: '/admin/logs', label: 'İşlem Geçmişi', icon: Activity },
  ]},
  { section: 'İçerik', items: [
    { to: '/admin/homepage', label: 'Anasayfa', icon: Home },
    { to: '/admin/menu', label: 'Menü Yönetimi', icon: ListTree },
    { to: '/admin/pages', label: 'Özel Sayfalar', icon: FilePlus },
    { to: '/admin/static-pages', label: 'Statik Sayfalar', icon: FileCode },
    { to: '/admin/page-texts', label: 'Sayfa Metinleri', icon: Type },
    { to: '/admin/hero', label: 'Hero Slider', icon: Sparkles },
    { to: '/admin/news', label: 'Duyurular', icon: Newspaper },
    { to: '/admin/presidents', label: 'Başkanlar', icon: Crown },
    { to: '/admin/board', label: 'Yönetim Kurulu', icon: Crown },
    { to: '/admin/milestones', label: 'Tarihçe (Zaman Çizelgesi)', icon: Calendar },
    { to: '/admin/projects', label: 'Projeler', icon: FolderKanban },
    { to: '/admin/events', label: 'Etkinlikler', icon: Calendar },
    { to: '/admin/press', label: 'Medya & Basın', icon: Tv },
    { to: '/admin/sponsors', label: 'Destekçiler', icon: Building2 },
    { to: '/admin/publications', label: 'Yayınlar', icon: BookOpen },
    { to: '/admin/kvkk-docs', label: 'KVKK Belgeleri', icon: FileText },
    { to: '/admin/tax-docs', label: 'Vergi Belgeleri', icon: FileText },
    { to: '/admin/faq', label: 'Burs SSS', icon: FileText },
    { to: '/admin/uploads', label: 'Görseller', icon: ImageIcon },
  ]},
  { section: 'Bağış & Kanallar', items: [
    { to: '/admin/donation-types', label: 'Bağış Türleri', icon: CircleDollarSign },
    { to: '/admin/special-days', label: 'Özel Günler', icon: Gift },
    { to: '/admin/bank-accounts', label: 'Banka Hesapları', icon: Banknote },
  ]},
  { section: 'Başvurular', items: [
    { to: '/admin/donations', label: 'Bağışlar', icon: Heart },
    { to: '/admin/scholarships', label: 'Burs Başvuruları', icon: GraduationCap },
    { to: '/admin/volunteers', label: 'Başvuru Formları', icon: HandHeart },
    { to: '/admin/volunteer-form-bireysel', label: 'Bireysel Form Alanları', icon: FilePlus },
    { to: '/admin/volunteer-form-kurumsal', label: 'Kurumsal Form Alanları', icon: FilePlus },
    { to: '/admin/messages', label: 'Mesajlar', icon: Mail },
  ]},
  { section: 'Ayarlar', items: [
    { to: '/admin/settings', label: 'Site Ayarları', icon: SettingsIcon },
    { to: '/admin/seo', label: 'SEO & Meta', icon: Globe },
    { to: '/admin/visibility', label: 'Sayfa Görünürlük', icon: Eye },
    { to: '/admin/email-templates', label: 'E-posta Şablonları', icon: MailCheck },
    { to: '/admin/users', label: 'Yöneticiler', icon: Users },
    { to: '/admin/backup', label: 'Yedekleme', icon: DatabaseBackup },
  ]},
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [unread, setUnread] = useState(0);
  const [live, setLive] = useState(0);
  const [publishing, setPublishing] = useState(false);

  const publishSite = async () => {
    setPublishing(true);
    try {
      const r = await api.bumpSiteVersion();
      toast.success(`Yayınlandı (v${r.version}) — açık ziyaretçi sayfaları ~1 dk içinde otomatik yenilenecek`);
    } catch {
      toast.error('Yayınlanamadı');
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('yekuv_token')) { nav('/admin/login'); return; }
    api.adminMe().then(setUser).catch(() => nav('/admin/login'));
  }, [nav]);

  useEffect(() => {
    const tick = () => {
      api.adminAnalyticsLive().then(d => setLive(d?.active_now || 0)).catch(() => {});
      api.adminMessages().then(list => setUnread(list.filter(m => !m.read).length)).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = () => {
    localStorage.removeItem('yekuv_token');
    nav('/admin/login');
  };

  return (
    <div className="min-h-screen bg-cream-200 text-ink-600 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 w-72 h-screen bg-ink-700 text-cream flex flex-col transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <div className="px-6 py-6 border-b border-cream/10 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logos/yekuv-logo-white.png" alt="YEKÜV" className="h-10 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div>
              <div className="font-display text-xl tracking-tight leading-none">YEKÜV</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold mt-1">Yönetim Paneli</div>
            </div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-cream/70 hover:text-cream" aria-label="Kapat">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-5 px-3 scrollbar-thin">
          {NAV.map(group => (
            <div key={group.section} className="mb-6">
              <div className="px-3 text-[10px] uppercase tracking-[0.3em] text-cream/40 font-bold mb-2">{group.section}</div>
              {group.items.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.to.replace('/admin', '').replace('/', '') || 'dashboard'}`}
                    className={({ isActive }) => `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors mb-0.5 ${isActive ? 'bg-gold text-ink-700 font-semibold shadow-lg shadow-gold/20' : 'text-cream/80 hover:bg-cream/10 hover:text-cream'}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" />
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User panel */}
        <div className="px-3 py-4 border-t border-cream/10">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-cream/5">
            <div className="w-9 h-9 rounded-full bg-gold text-ink-700 grid place-items-center font-display font-bold text-sm">
              {user?.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.username || 'admin'}</div>
              <div className="text-[10px] uppercase tracking-widest text-cream/50">Admin</div>
            </div>
            <button onClick={logout} className="text-cream/60 hover:text-coral-200" title="Çıkış" data-testid="admin-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-cream/95 backdrop-blur border-b border-ink-100">
          <div className="flex items-center gap-3 px-5 lg:px-8 h-16">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-ink-600 hover:text-ink-500" aria-label="Menü">
              <Menu className="w-5 h-5" />
            </button>

            <div className="hidden md:flex items-center gap-2 bg-white border border-ink-100 rounded-xl px-3 h-10 w-80 max-w-full">
              <Search className="w-4 h-4 text-ink-400 shrink-0" />
              <input className="bg-transparent flex-1 text-sm outline-none placeholder:text-ink-300" placeholder="Hızlı arama (yakında)..." disabled />
              <kbd className="text-[10px] tracking-wider text-ink-400 bg-cream-200 px-1.5 py-0.5 rounded">⌘K</kbd>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={publishSite}
                disabled={publishing}
                data-testid="publish-site-btn"
                title="Tüm ziyaretçilerin açık sayfaları ~1 dakika içinde otomatik yenilenir"
                className="inline-flex items-center gap-2 bg-gold text-ink-700 px-4 h-10 rounded-xl text-xs font-bold hover:bg-gold-400 transition-colors disabled:opacity-60"
              >
                <Radio className="w-4 h-4" /> {publishing ? 'Yayınlanıyor…' : 'Değişiklikleri Yayınla'}
              </button>

              <a href="/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex items-center gap-2 text-xs text-ink-500 hover:text-ink-600 px-3 h-10 rounded-xl border border-ink-100 hover:border-ink-300 transition-colors">
                Siteyi Gör <ChevronRight className="w-3.5 h-3.5" />
              </a>

              {/* Live indicator */}
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 h-10 rounded-xl border border-emerald-100" data-testid="live-counter">
                <span className="relative flex w-2.5 h-2.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75"></span>
                  <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                </span>
                <span className="text-xs font-semibold tabular">{live} canlı</span>
              </div>

              {/* Bell with unread */}
              <Link to="/admin/messages" className="relative w-10 h-10 grid place-items-center rounded-xl border border-ink-100 hover:border-ink-300 text-ink-500 hover:text-ink-600 transition-colors">
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] grid place-items-center px-1 rounded-full bg-coral-300 text-white text-[10px] font-bold">{unread}</span>
                )}
              </Link>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-5 lg:px-8 py-6 lg:py-8 overflow-x-hidden">
          <Outlet context={{ user }} />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-ink-900/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
}
